import { deriveResourceType, type WhatIfChange, type WhatIfPropertyChange } from "./whatif.js";

export interface NoiseRule { resourceType: string; pathPattern: RegExp; reason: string }

// Real, intended changes that an approved apply SHOULD make. Shown, not failed.
export const KNOWN_PENDING: NoiseRule[] = [
  { resourceType: "Microsoft.ServiceBus/namespaces", pathPattern: /(^|\.)minimumTlsVersion$/, reason: "TLS 1.0→1.2 (the #64 fix); apply will enforce it" },
];

// Cosmetic / RP-owned / unanalyzable lines that an apply will NOT meaningfully change.
export const KNOWN_NOISE: NoiseRule[] = [
  { resourceType: "Microsoft.KeyVault/vaults", pathPattern: /(^|\.)networkAcls$/, reason: "declares the effective default (AzureServices/Allow); verified apply-time no-op" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)ingress\.exposedPort$/, reason: "RP runtime field" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)ingress\.traffic$/, reason: "RP runtime field" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)runningStatus$/, reason: "RP runtime status" },
  { resourceType: "Microsoft.App/managedEnvironments", pathPattern: /(^|\.)peerAuthentication$/, reason: "RP default" },
  { resourceType: "Microsoft.App/managedEnvironments", pathPattern: /(^|\.)peerTrafficConfiguration$/, reason: "RP default" },
  { resourceType: "Microsoft.CognitiveServices/accounts/connections", pathPattern: /(^|\.)credentials$/, reason: "write-only credential — always re-shows" },
  { resourceType: "Microsoft.CognitiveServices/accounts/connections", pathPattern: /(^|\.)(group|isDefault|peRequirement|peStatus|useWorkspaceManagedIdentity)$/, reason: "RP connection metadata" },
  { resourceType: "Microsoft.Insights/components", pathPattern: /(^|\.)(Flow_Type|Request_Source)$/, reason: "RP-stamped" },
  { resourceType: "Microsoft.ServiceBus/namespaces/queues", pathPattern: /(^|\.)(autoDeleteOnIdle|duplicateDetectionHistoryTimeWindow|maxMessageSizeInKilobytes|maxSizeInMegabytes)$/, reason: "RP default the bicep doesn't declare" },
];

export interface ClassifiedChange {
  resourceType: string; resourceName: string; path: string;
  before?: unknown; after?: unknown; reason?: string;
}
export interface ClassifiedResult { noise: ClassifiedChange[]; pending: ClassifiedChange[]; unexpected: ClassifiedChange[] }

const shortName = (resourceId: string) =>
  resourceId.startsWith("[") ? "(role assignment)" : resourceId.split("/").pop() ?? resourceId;

// An ARM template expression like "[reference(...).x]" / "[format(...)]" — a
// cosmetic what-if artifact that resolves to the same value at deploy time.
function isArmExpression(v: unknown): boolean {
  return typeof v === "string" && /^\[.*\b(reference|format|guid|subscriptionResourceId|extensionResourceId)\(/.test(v) && v.endsWith("]");
}

// Flatten a delta tree into leaf property changes with dotted/indexed paths.
function flattenDelta(delta: WhatIfPropertyChange[], prefix = ""): WhatIfPropertyChange[] {
  const out: WhatIfPropertyChange[] = [];
  for (const d of delta) {
    const segment = /^[0-9]+$/.test(d.path) ? `[${d.path}]` : (prefix ? `.${d.path}` : d.path);
    const path = prefix ? `${prefix}${segment}` : d.path;
    if (d.children && d.children.length > 0) out.push(...flattenDelta(d.children, path));
    else out.push({ ...d, path });
  }
  return out;
}

/** Classify each what-if change into noise / pending / unexpected. Pure. */
export function classifyWhatIf(changes: WhatIfChange[]): ClassifiedResult {
  const noise: ClassifiedChange[] = [], pending: ClassifiedChange[] = [], unexpected: ClassifiedChange[] = [];
  for (const c of changes) {
    if (c.changeType === "NoChange" || c.changeType === "Ignore") continue; // not drift
    if (c.changeType === "Create" || c.changeType === "Delete") {
      // A whole new/removed resource is always signal — never let per-property
      // noise rules swallow it.
      unexpected.push({ resourceType: deriveResourceType(c.resourceId), resourceName: shortName(c.resourceId), path: `(resource ${c.changeType.toLowerCase()})` });
      continue;
    }
    if (c.changeType === "Unsupported") {
      const entry: ClassifiedChange = { resourceType: "(unsupported)", resourceName: shortName(c.resourceId), path: "(whole resource)" };
      if (c.resourceId.includes('Microsoft.Authorization/roleAssignments')) noise.push({ ...entry, reason: "role assignment GUID from reference() — unanalyzable" });
      else unexpected.push(entry);
      continue;
    }
    const resourceType = deriveResourceType(c.resourceId);
    const resourceName = shortName(c.resourceId);
    for (const leaf of flattenDelta(c.delta ?? [])) {
      const base: ClassifiedChange = { resourceType, resourceName, path: leaf.path, before: leaf.before, after: leaf.after };
      if (leaf.propertyChangeType === "NoEffect") { noise.push({ ...base, reason: "NoEffect (no-op)" }); continue; }
      if (isArmExpression(leaf.after)) { noise.push({ ...base, reason: "reference()/format() artifact — same value" }); continue; }
      const pend = KNOWN_PENDING.find((rule) => rule.resourceType === resourceType && rule.pathPattern.test(leaf.path));
      if (pend) { pending.push({ ...base, reason: pend.reason }); continue; }
      const n = KNOWN_NOISE.find((rule) => rule.resourceType === resourceType && rule.pathPattern.test(leaf.path));
      if (n) { noise.push({ ...base, reason: n.reason }); continue; }
      unexpected.push(base);
    }
  }
  return { noise, pending, unexpected };
}
